import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp } from "../lib/format";
import { Modal, PrimaryButton, QuietButton } from "./UI";
import { useMaterialUnits } from "../hooks/useMaterialUnits";

/* LT-20 — per-line "Refresh from library". The line's snapshotted quantity
 * inputs never move on their own; this shows old → new for every field
 * that would change and applies only after confirmation. */
function fmtValue(field, v, unitLabelFor) {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "is_divisible_applied") return v ? "yes" : "no";
  if (field === "wastage_source") return String(v).replace("_", " ");
  if (field === "coverage_unit_applied" || field === "unit_key_applied") return unitLabelFor(v) || String(v);
  if (field === "wastage_pct_applied" || field === "material_wastage_pct_applied") return `${v}%`;
  return String(v);
}
export { fmtValue };

export function RefreshFromLibraryDialog({ open, tenderId, line, onClose, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const { labelFor: unitLabelFor } = useMaterialUnits();

  useEffect(() => {
    if (!open || !line) return undefined;
    let alive = true;
    setPlan(null); setError(null); setLoading(true);
    api.get(`/tenders/${tenderId}/lines/${line.id}/refresh-preview`)
      .then((r) => { if (alive) setPlan(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Could not compare with the library"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, tenderId, line]);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/tenders/${tenderId}/lines/${line.id}/refresh`);
      toast.success(r.data.changed.length ? `Line refreshed — ${r.data.changed.length} material(s) updated` : "Line already matches the library");
      onApplied?.(r.data.tender);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setApplying(false);
    }
  };

  const rateMoves = plan && plan.material_rate_before != null && plan.material_rate_after != null
    && Math.abs(Number(plan.material_rate_before) - Number(plan.material_rate_after)) >= 0.005;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Refresh this line from the Materials library?"
      testid="refresh-from-library-modal"
      maxWidth={640}
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="refresh-cancel">Cancel</QuietButton>
          <PrimaryButton
            onClick={apply}
            disabled={applying || loading || !plan || !plan.has_changes}
            icon={applying ? Loader2 : RefreshCw}
            data-testid="refresh-confirm"
          >
            {plan && !plan.has_changes ? "Nothing to refresh" : "Apply refresh"}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-2 mb-3" style={{ fontSize: 12.5 }}>
        {line?.ref ? `Line ${line.ref} — ` : ""}{line?.description}. The values frozen on this line when it was
        inserted are compared with the library now. Nothing changes until you confirm.
      </p>
      {loading && <p className="text-ink-3" style={{ fontSize: 12 }}>Comparing…</p>}
      {error && <p className="text-note-red" style={{ fontSize: 12 }} data-testid="refresh-error">{error}</p>}
      {plan && (
        <div className="space-y-3" data-testid="refresh-plan">
          {plan.entries.map((e) => (
            <div key={e.entry_id} className="rounded-md border border-line">
              <div className="flex items-center justify-between px-3 py-1.5 bg-surface-2/60 border-b border-line-soft">
                <span className="text-ink" style={{ fontSize: 12, fontWeight: 600 }}>{e.description || "(material)"}</span>
                <span className="text-ink-3" style={{ fontSize: 10.5 }}>
                  {!e.linked ? "not linked to a library material — nothing to refresh"
                    : !e.material_found ? "library material not found — §2 defaults apply"
                    : e.changes.length ? `${e.changes.length} change(s)` : "up to date"}
                </span>
              </div>
              {e.changes.length > 0 && (
                <table className="w-full" style={{ fontSize: 11.5 }}>
                  <tbody>
                    {e.changes.map((c) => (
                      <tr key={c.field} className="border-t border-line-soft" data-testid={`refresh-change-${e.entry_id}-${c.field}`}>
                        <td className="px-3 py-1 text-ink-2" style={{ width: "40%" }}>{c.label}</td>
                        <td className="px-2 py-1 text-right font-mono tnum text-ink-3 line-through">{fmtValue(c.field, c.old, unitLabelFor)}</td>
                        <td className="px-1 py-1 text-center text-ink-4">→</td>
                        <td className="px-3 py-1 font-mono tnum text-ink" style={{ fontWeight: 600 }}>{fmtValue(c.field, c.new, unitLabelFor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          <div className="rounded-md bg-surface-2/60 px-3 py-2 text-ink-2" style={{ fontSize: 11.5 }} data-testid="refresh-rate-summary">
            Material rate per unit: <span className="font-mono tnum">{gbp(plan.material_rate_before || 0)}</span>
            {" → "}
            <span className="font-mono tnum" style={{ fontWeight: 600 }}>{gbp(plan.material_rate_after || 0)}</span>
            {rateMoves ? " — the line cost will follow." : " — unchanged."}
            {plan.material_rate_override && " Your manual material-rate override on this line is kept."}
          </div>
        </div>
      )}
    </Modal>
  );
}
